import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  CATEGORIES_MODULE,
  MODULES_COEUR,
  MODULES_PAR_CATEGORIE,
  REGISTRE_MODULES,
} from '@artosera/shared';
import { Badge, Button, Dialog, Input, Switch, Textarea, useToast } from '@artosera/ui';
import { basculerInterrupteur, obtenirInterrupteurs } from '../lib/api.js';
import { formaterDate } from '../lib/format.js';
import {
  coupeEnUrgence,
  correspond,
  etatModule,
  palierVerrouillant,
  resumer,
  type FiltreEtat,
} from '../lib/interrupteurs.js';
import { useLibellesModules } from '../lib/modules.js';
import { usePeut } from '../auth/usePeut.js';
import type { Interrupteurs as VueInterrupteurs } from '../types.js';
import { IaInterrupteurs } from './IaInterrupteurs.js';
import pageStyles from '../styles/page.module.css';
import formStyles from '../styles/form.module.css';
import styles from './Interrupteurs.module.css';

const MOTIF_MIN = 3;
const MOTIF_MAX = 500;

interface Cible {
  module: string;
  coupe: boolean;
}

/**
 * Interrupteurs globaux (D21, ARTO-56) : couper un module complémentaire pour
 * toutes les galeries, ou le rétablir. Réservé à la direction de Venio
 * (`console:kill-switch`) ; le motif est obligatoire et part au journal. La
 * variable de secours `ARTOSERA_MODULES_COUPES` est montrée, pas modifiable.
 */
export function Interrupteurs(): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const autorise = peut('console:kill-switch');

  if (!autorise) {
    return (
      <section>
        <div className={pageStyles.entete}>
          <div>
            <h1>{t('interrupteurs.refusTitre')}</h1>
            <p>{t('interrupteurs.refusTexte')}</p>
          </div>
        </div>
        <IaInterrupteurs />
      </section>
    );
  }

  return <PageInterrupteurs />;
}

function PageInterrupteurs(): JSX.Element {
  const { t } = useTranslation('console');
  const libelles = useLibellesModules();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [cible, setCible] = useState<Cible | null>(null);
  const [motif, setMotif] = useState('');
  const [erreurMotif, setErreurMotif] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState<FiltreEtat>('tous');

  const requete = useQuery({
    queryKey: ['console', 'interrupteurs'],
    queryFn: obtenirInterrupteurs,
  });

  const fermer = (): void => {
    setCible(null);
    setMotif('');
    setErreurMotif(null);
  };

  const mutation = useMutation({
    mutationFn: ({ module, coupe, motif: texte }: Cible & { motif: string }) =>
      basculerInterrupteur(module, coupe, texte),
    onMutate: () => setErreur(null),
    onSuccess: async (reponse: VueInterrupteurs, { module, coupe }) => {
      queryClient.setQueryData(['console', 'interrupteurs'], reponse);
      fermer();
      // Les listes effectives de toutes les galeries changent.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['console', 'galeries'] }),
        queryClient.invalidateQueries({ queryKey: ['console', 'galerie'] }),
      ]);
      toast({
        title: coupe
          ? t('interrupteurs.succesCoupe', { module: libelles.libelle(module) })
          : t('interrupteurs.succesRetabli', { module: libelles.libelle(module) }),
        closeLabel: t('interrupteurs.fermer'),
      });
    },
    onError: () => {
      fermer();
      setErreur(t('interrupteurs.erreurGenerique'));
    },
  });

  const envoyer = (event: FormEvent): void => {
    event.preventDefault();
    if (!cible) return;
    const texte = motif.trim();
    if (texte.length < MOTIF_MIN) {
      setErreurMotif(t('interrupteurs.erreurMotif'));
      return;
    }
    setErreurMotif(null);
    mutation.mutate({ ...cible, motif: texte });
  };

  const donnees = requete.data;
  const filtreActif = filtre !== 'tous' || recherche.trim() !== '';
  const nombreAffiches = donnees
    ? CATEGORIES_MODULE.reduce(
        (total, categorie) =>
          total +
          MODULES_PAR_CATEGORIE[categorie].filter((module) =>
            correspond(module, donnees, filtre, recherche, {
              libelle: libelles.libelle(module),
              description: libelles.description(module),
            }),
          ).length,
        0,
      )
    : 0;
  const nomCible = cible ? libelles.libelle(cible.module) : '';

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('interrupteurs.titre')}</h1>
          <p>{t('interrupteurs.texte')}</p>
        </div>
      </div>

      {erreur ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {erreur}
        </p>
      ) : null}

      {requete.isLoading ? (
        <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>
      ) : requete.isError || !donnees ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {t('interrupteurs.erreurChargement')}
        </p>
      ) : (
        <>
          <Resume donnees={donnees} />

          <section className={styles.coeur} aria-labelledby="interrupteurs-coeur">
            <div className={styles.enteteCoeur}>
              <h2 id="interrupteurs-coeur" className={styles.titreSection}>
                {t('interrupteurs.coeurTitre')}{' '}
                <span className={styles.nombre}>({MODULES_COEUR.length})</span>
              </h2>
              <p className={styles.texteSection}>{t('interrupteurs.coeurTexte')}</p>
            </div>
            <ul className={styles.listeCoeur}>
              {MODULES_COEUR.map((module) => (
                <li key={module} className={styles.puceCoeur} data-coeur={module}>
                  <span className={styles.nomCoeur}>
                    <IconeCoche />
                    {libelles.libelle(module)}
                  </span>
                  <span className={styles.descriptionCoeur}>{libelles.description(module)}</span>
                  <span className={styles.toujoursActif}>{t('interrupteurs.toujoursActif')}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className={styles.pilotables} aria-labelledby="interrupteurs-pilotables">
            <h2 id="interrupteurs-pilotables" className={styles.titrePilotables}>
              {t('interrupteurs.pilotablesTitre')}
            </h2>
            <p className={styles.portee}>{t('interrupteurs.portee')}</p>

            <div className={styles.barreFiltres}>
              <div className={styles.recherche}>
                <label htmlFor="interrupteurs-recherche">{t('interrupteurs.recherche')}</label>
                <Input
                  id="interrupteurs-recherche"
                  type="search"
                  value={recherche}
                  placeholder={t('interrupteurs.recherchePlaceholder')}
                  autoComplete="off"
                  onChange={(event) => setRecherche(event.target.value)}
                />
              </div>
              <div
                className={styles.filtresEtat}
                role="group"
                aria-label={t('interrupteurs.filtreEtat')}
              >
                {(donnees.modules
                  ? (['tous', 'coupes', 'verrouilles', 'avenir'] as const)
                  : (['tous', 'coupes', 'avenir'] as const)
                ).map((valeur) => (
                  <Button
                    key={valeur}
                    size="sm"
                    variant={filtre === valeur ? 'primary' : 'secondary'}
                    aria-pressed={filtre === valeur}
                    onClick={() => setFiltre(valeur)}
                  >
                    {t(`interrupteurs.filtre.${valeur}`)}
                  </Button>
                ))}
              </div>
            </div>

            {filtreActif ? (
              <p role="status" className={styles.compteResultats}>
                {t('interrupteurs.resultats', { count: nombreAffiches })}
              </p>
            ) : null}
            {filtreActif && nombreAffiches === 0 ? (
              <p className={pageStyles.videEtat}>{t('interrupteurs.aucunResultat')}</p>
            ) : null}

            <div className={styles.catalogue}>
              {CATEGORIES_MODULE.map((categorie) => {
                const modules = MODULES_PAR_CATEGORIE[categorie].filter((module) =>
                  correspond(module, donnees, filtre, recherche, {
                    libelle: libelles.libelle(module),
                    description: libelles.description(module),
                  }),
                );
                if (modules.length === 0) return null;
                return (
                  <section
                    key={categorie}
                    className={`${pageStyles.panneau} ${styles.panneauCategorie}`}
                    aria-labelledby={`interrupteurs-${categorie}`}
                  >
                    <h3 id={`interrupteurs-${categorie}`} className={styles.titreCategorie}>
                      {t(`interrupteurs.categories.${categorie}`)}{' '}
                      <span className={styles.nombre}>({modules.length})</span>
                    </h3>
                    <ul className={styles.listeModules}>
                      {modules.map((module) => {
                        const coupure = donnees.coupes.find((ligne) => ligne.module === module);
                        const urgence = coupeEnUrgence(module, donnees);
                        const secours = urgence === 'secours';
                        const definition = REGISTRE_MODULES[module];
                        // `disponible: false` : module en construction (modules.md § 7).
                        const etat = etatModule(module, donnees);
                        const accessible = etat === 'ouvert';
                        const palierBloque = palierVerrouillant(module, donnees);
                        // Verrouillé par son palier et sans coupure à lever : rien à couper,
                        // le commutateur reste hors d'atteinte (cadenas) jusqu'au déblocage.
                        const riendACouper = etat === 'palier' && urgence === null;
                        const descriptionId = `interrupteur-${module}-description`;
                        return (
                          <li
                            key={module}
                            className={styles.module}
                            data-module={module}
                            data-etat={etat}
                          >
                            <div className={styles.enteteModule}>
                              <Switch
                                id={`interrupteur-${module}`}
                                label={libelles.libelle(module)}
                                checked={accessible}
                                disabled={!definition.disponible || mutation.isPending}
                                onCheckedChange={(autorise) =>
                                  setCible({ module, coupe: !autorise })
                                }
                                tone={urgence !== null ? 'danger' : 'default'}
                                locked={secours || riendACouper}
                                pending={mutation.isPending && cible?.module === module}
                                aria-describedby={descriptionId}
                              />
                              <span className={styles.pastilles}>
                                {definition.famille === 'service' ? (
                                  <Badge>{t('interrupteurs.service')}</Badge>
                                ) : null}
                                <Badge
                                  tone={
                                    etat === 'bientot' || etat === 'palier'
                                      ? 'withdrawn'
                                      : etat === 'ouvert'
                                        ? 'available'
                                        : 'sold'
                                  }
                                >
                                  {etat === 'bientot'
                                    ? t('interrupteurs.bientot')
                                    : etat === 'palier' && palierBloque
                                      ? t('interrupteurs.etat.palier', {
                                          palier: t(`paliers.court.${palierBloque}`),
                                        })
                                      : etat === 'secours'
                                        ? t('interrupteurs.etat.secours')
                                        : etat === 'coupe'
                                          ? t('interrupteurs.etat.coupe')
                                          : t('interrupteurs.etat.ouvert')}
                                </Badge>
                                {etat === 'palier' && urgence !== null ? (
                                  <Badge tone="sold">
                                    {urgence === 'secours'
                                      ? t('interrupteurs.etat.secours')
                                      : t('interrupteurs.etat.coupe')}
                                  </Badge>
                                ) : null}
                              </span>
                            </div>
                            <div id={descriptionId} className={styles.description}>
                              <p>{libelles.description(module)}</p>
                              {definition.dependances.length > 0 ? (
                                <p>
                                  {t('fiche.modules.necessite', {
                                    liste: libelles.liste(definition.dependances),
                                  })}
                                </p>
                              ) : null}
                              {!definition.disponible ? (
                                <p>{t('fiche.modules.bientotTexte')}</p>
                              ) : null}
                              {etat === 'palier' ? (
                                <>
                                  <p>{t('interrupteurs.palierExplication')}</p>
                                  {urgence !== null ? (
                                    <p>{t('interrupteurs.palierEtCoupe')}</p>
                                  ) : null}
                                  <p>
                                    <Link to="/paliers" className={styles.lienPalier}>
                                      {t('interrupteurs.palierLien')}
                                    </Link>
                                  </p>
                                </>
                              ) : null}
                              {coupure ? (
                                <p>
                                  {t('interrupteurs.detailCoupe', {
                                    auteur: coupure.auteurEmail,
                                    date: formaterDate(coupure.coupeLe, libelles.langue),
                                    motif: coupure.motif,
                                  })}
                                </p>
                              ) : null}
                              {secours ? <p>{t('interrupteurs.detailSecours')}</p> : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          </section>

          <section className={`${pageStyles.panneau} ${styles.secours}`} aria-labelledby="secours">
            <h2 id="secours" className={styles.titreSection}>
              {t('interrupteurs.secoursTitre')}
            </h2>
            <p className={styles.texteSection}>{t('interrupteurs.secoursTexte')}</p>
            {donnees.coupesSecours.length > 0 ? (
              <ul className={styles.listeSecours}>
                {donnees.coupesSecours.map((module) => (
                  <li key={module}>
                    <Badge tone="sold">{libelles.libelle(module)}</Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.texteSection}>{t('interrupteurs.secoursAucun')}</p>
            )}
          </section>
        </>
      )}

      <IaInterrupteurs />
      <Dialog
        open={cible !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) fermer();
        }}
        title={
          cible?.coupe
            ? t('interrupteurs.couperTitre', { module: nomCible })
            : t('interrupteurs.retablirTitre', { module: nomCible })
        }
        description={
          cible?.coupe ? t('interrupteurs.couperTexte') : t('interrupteurs.retablirTexte')
        }
        closeLabel={t('interrupteurs.fermer')}
      >
        <form onSubmit={envoyer} noValidate>
          <div className={formStyles.champ}>
            <label htmlFor="interrupteur-motif">{t('interrupteurs.champMotif')}</label>
            <Textarea
              id="interrupteur-motif"
              rows={3}
              maxLength={MOTIF_MAX}
              required
              value={motif}
              aria-invalid={erreurMotif ? true : undefined}
              aria-describedby={
                erreurMotif
                  ? 'interrupteur-motif-aide interrupteur-motif-erreur'
                  : 'interrupteur-motif-aide'
              }
              onChange={(event) => setMotif(event.target.value)}
            />
            <p id="interrupteur-motif-aide" className={formStyles.aide}>
              {t('interrupteurs.motifAide')}
            </p>
            {erreurMotif ? (
              <p id="interrupteur-motif-erreur" role="alert" className={formStyles.erreur}>
                {erreurMotif}
              </p>
            ) : null}
          </div>
          <div className={formStyles.actionsFormulaire}>
            <Button type="button" variant="secondary" onClick={fermer}>
              {t('interrupteurs.annuler')}
            </Button>
            <Button
              type="submit"
              variant={cible?.coupe ? 'danger' : 'primary'}
              loading={mutation.isPending}
            >
              {mutation.isPending
                ? t('interrupteurs.enCours')
                : cible?.coupe
                  ? t('interrupteurs.confirmerCoupe')
                  : t('interrupteurs.confirmerRetablir')}
            </Button>
          </div>
        </form>
      </Dialog>
    </section>
  );
}

/** Trois chiffres : modules pilotables, coupés pour tout le parc, cœur. */
function Resume({ donnees }: { donnees: VueInterrupteurs }): JSX.Element {
  const { t } = useTranslation('console');
  const resume = resumer(donnees);
  return (
    <div
      className={styles.resume}
      role="group"
      aria-label={t('interrupteurs.resume.titre')}
      data-tuiles={resume.nonDebloques !== null ? '4' : '3'}
    >
      <div className={styles.tuile}>
        <span className={styles.chiffre}>{resume.pilotables}</span>
        <span className={styles.etiquette}>
          {t('interrupteurs.resume.pilotables', { count: resume.pilotables })}
        </span>
      </div>
      <div className={styles.tuile} data-alerte={resume.coupes > 0 ? 'oui' : undefined}>
        <span className={styles.chiffre}>{resume.coupes}</span>
        <span className={styles.etiquette}>
          {t('interrupteurs.resume.coupes', { count: resume.coupes })}
        </span>
      </div>
      {resume.nonDebloques !== null ? (
        <div className={styles.tuile}>
          <span className={styles.chiffre}>{resume.nonDebloques}</span>
          <span className={styles.etiquette}>
            {t('interrupteurs.resume.nonDebloques', { count: resume.nonDebloques })}
          </span>
        </div>
      ) : null}
      <div className={styles.tuile}>
        <span className={styles.chiffre}>{resume.coeur}</span>
        <span className={styles.etiquette}>{t('interrupteurs.resume.coeur')}</span>
      </div>
    </div>
  );
}

function IconeCoche(): JSX.Element {
  return (
    <svg
      className={styles.coche}
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 8.5l3.2 3.2L13 4.8" />
    </svg>
  );
}
